import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form, Field, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import ReactLoading from 'react-loading'
import { Input, Button } from '../atoms/'
import apis from '../../libs/services'
import { useAuth } from '../../hooks/useAuth'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { KeyboardControl, ControlHighlight } from '../../components/molecules/KeyboardControls'

interface EditLineProps {
  id: string
  isEditable?: boolean
  label: string
  onPasswordSave?: Function
  onPasswordSaveComplete?: Function
  type?: string
  value: string
  setEditing?: Dispatch<SetStateAction<boolean>>
  isSentryMasked?: boolean
};

const initialPasswordFormValues = {
  oldPassword: '',
  newPassword: ''
}

const initialJobTitleFormValues = {
  jobTitle: ''
}

const PasswordFormSchema = Yup.object().shape({
  oldPassword: Yup.string().required('This field is required!'),
  newPassword: Yup.string().required('This field is required!')
})

const JobTitleFormSchema = Yup.object().shape({
  jobTitle: Yup.string().required('This field is required!')
})

const EditLine: React.FC<EditLineProps> = ({
  id,
  isEditable = false,
  label,
  onPasswordSave,
  onPasswordSaveComplete,
  type = 'text',
  value,
  setEditing,
  isSentryMasked = false
}: EditLineProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const wasEditing = useRef<boolean>(false)
  const { setUserProfile } = useAuth()
  const keyboardControls = useKeyboardControls()

  const memorizedValue = useMemo(() => {
    if (type === 'password') {
      return '************'
    }
    return value
  }, [value, type])

  const handleClick = useCallback(() => {
    setIsEditing(false)
    setEditing?.(false)
  }, [setEditing])

  const handlePasswordSave = useCallback(async (values: any) => {
    if (typeof onPasswordSave === 'function') {
      onPasswordSave()
    }
    await apis.changePassword({
      old_password: values.oldPassword,
      new_password: values.newPassword
    })
    if (typeof onPasswordSaveComplete === 'function') {
      onPasswordSaveComplete()
    }
    setIsEditing(false)
  }, [onPasswordSave, onPasswordSaveComplete, setIsEditing])

  const handleProfileSave = useCallback(async (values: any) => {
    await apis.updateUserProfile({
      job_title: values.jobTitle,
      schoolYear: ''
    })
    const profileRes = await apis.getUserProfile()
    setUserProfile(profileRes?.data?.data)
    setIsEditing(false)
  }, [setUserProfile, setIsEditing])

  const idLabel = `${id}-label`
  const idField = `${id}-field`
  const idFieldOldPassword = `${id}-field-old-password`
  const idFieldNewPassword = `${id}-field-new-password`
  const idButtonEdit = `${id}-button-edit`
  const idButtonCancel = `${id}-button-cancel`
  const idButtonSave = `${id}-button-save`

  useEffect(() => {
    if (isEditing && !(wasEditing.current)) {
      keyboardControls.setFocusedElement(idFieldOldPassword)
    }

    wasEditing.current = isEditing
  }, [isEditing, idFieldOldPassword, keyboardControls])

  return (
    <div id={id}>
      <div className='flex flex-wrap mt-[32px] items-start'>
        <div
          id={idLabel}
          data-testid={idLabel}
          tabIndex={-1}
          className='max-w-[128px] w-full text-white text-[18px] font-semibold'
        >
          {label}
        </div>
        <div className={`flex-1 text-white text-[18px] max-w-[356px] ml-6${isSentryMasked ? ' sentry-mask' : ''}`}>
          <div className='min-h-[27px]'>{memorizedValue}</div>
        </div>
        {isEditable && (
          <div className='flex-shrink-0 flex-grow-0 text-[#34e89e] ml-6'>
            <ControlHighlight
              id={`highlight-${idButtonEdit}`}
              data-testid={`highlight-${idButtonEdit}`}
              active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idButtonEdit)}
              highlightEffect={HighlightEffect.ButtonHexOutline}
            >
              <KeyboardControl>
                <button
                  id={idButtonEdit}
                  data-testid={idButtonEdit}
                  className='cursor-pointer'
                  onClick={() => {
                    setIsEditing(!isEditing)
                    setEditing?.(!isEditing)
                  }}
                >
                  <span>Edit&nbsp;&gt;</span>
                </button>
              </KeyboardControl>
            </ControlHighlight>
          </div>
        )}
        <div className='basis-full h-0' />
        <div className='max-w-[128px] w-full' />
        <div className='flex-1 text-white text-[18px] max-w-[356px] ml-6'>
          {isEditing && type === 'password' && (
            <Formik
              initialValues={initialPasswordFormValues}
              validationSchema={PasswordFormSchema}
              onSubmit={handlePasswordSave}
              key={1}
            >
              {({ errors, touched, isSubmitting }: FormikProps<{
                oldPassword: string
                newPassword: string
              }>) => (
                <Form>
                  <div className='mt-[18px]'>
                    <Field name='oldPassword'>
                      {({
                        field // { name, value, onChange, onBlur }
                      }: any) => (
                        <ControlHighlight
                          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idFieldOldPassword)}
                          data-testid={`highlight-${idFieldOldPassword}`}
                          highlightEffect={HighlightEffect.InputOutline}
                        >
                          <KeyboardControl>
                            <Input
                              id={idFieldOldPassword}
                              data-testid={idFieldOldPassword}
                              className='w-full py-[14px] px-[16px] text-[18px]'
                              type='password'
                              placeholder='Enter old password'
                              {...field}
                            />
                          </KeyboardControl>
                        </ControlHighlight>
                      )}
                    </Field>
                    {(errors.oldPassword != null && errors.oldPassword.trim() !== '') && (touched.oldPassword === true) ? (<div className='text-[#ff0000] pt-1'>{errors.oldPassword}</div>) : null}
                    <Field name='newPassword'>
                      {({
                        field // { name, value, onChange, onBlur }
                      }: any) => (
                        <ControlHighlight
                          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idFieldNewPassword)}
                          highlightEffect={HighlightEffect.InputOutline}
                        >
                          <KeyboardControl>
                            <Input
                              id={idFieldNewPassword}
                              data-testid={idFieldNewPassword}
                              className='w-full py-[14px] px-[16px] mt-4 text-[18px] border-dopage-primary'
                              type='password'
                              placeholder='Enter new password'
                              {...field}
                            />
                          </KeyboardControl>
                        </ControlHighlight>
                      )}
                    </Field>
                    {(errors.newPassword != null && errors.newPassword.trim() !== '') && (touched.newPassword === true) ? (<div className='text-[#ff0000] pt-1'>{errors.newPassword}</div>) : null}
                    <div className='flex'>
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idButtonCancel)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                        className='w-full'
                      >
                        <KeyboardControl>
                          <Button
                            id={idButtonCancel}
                            data-testid={idButtonCancel}
                            bgColor='bg-dopage-lightGreen'
                            className='w-full diamond-btn-sm p-[1px] h-[32px] font-bold mt-[16px]'
                            onClick={handleClick}
                          >
                            <div className='bg-neutral-2c w-full h-full flex justify-center items-center diamond-btn-sm'>
                              {isSubmitting
                                ? <ReactLoading type='bubbles' color='black' height={64} width={64} />
                                : <span className='font-normal text-[18px] text-[rgba(223, 248, 255, 1)]'>Cancel</span>}
                            </div>
                          </Button>
                        </KeyboardControl>
                      </ControlHighlight>
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idButtonSave)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                        className='w-full ml-4'
                      >
                        <KeyboardControl>
                          <Button
                            id={idButtonSave}
                            data-testid={idButtonSave}
                            type='submit'
                            className='h-[32px] diamond-btn-sm w-full font-bold mt-[16px]'
                            bgColor='bg-dopage-darkGreen'
                          >
                            <span className='font-normal text-[18px] text-neutral-900'>Save</span>
                          </Button>
                        </KeyboardControl>
                      </ControlHighlight>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
          {isEditing && type !== 'password' && (
            <div className='mt-[18px]'>
              <Formik
                initialValues={initialJobTitleFormValues}
                validationSchema={JobTitleFormSchema}
                onSubmit={handleProfileSave}
                key={2}
              >
                {({ errors, touched, isSubmitting }: FormikProps<{
                  jobTitle: string
                }>) => (
                  <Form>
                    <Field name='jobTitle'>
                      {({
                        field // { name, value, onChange, onBlur }
                      }: any) => (
                        <ControlHighlight
                          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idField)}
                          highlightEffect={HighlightEffect.InputOutline}
                        >
                          <KeyboardControl>
                            <Input
                              id={idField}
                              data-testid={idField}
                              className='w-full py-[14px] px-[16px] text-[18px]'
                              type='text'
                              placeholder='New Job Title'
                              {...field}
                            />
                          </KeyboardControl>
                        </ControlHighlight>
                      )}
                    </Field>
                    {(errors.jobTitle != null && errors.jobTitle.trim() !== '') && (touched.jobTitle === true) ? <div className='text-[#ff0000] pt-1'>{errors?.jobTitle}</div> : null}
                    <div className='flex w-full'>
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idButtonCancel)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                      >
                        <KeyboardControl>
                          <Button
                            id={idButtonCancel}
                            data-testid={idButtonCancel}
                            bgColor='bg-dopage-lightGreen'
                            wrapperClass='w-full'
                            className='w-full diamond-btn-sm p-[1px] h-[32px] font-bold mt-[16px]'
                            onClick={handleClick}
                          >
                            <div className='bg-neutral-2c w-full h-full flex justify-center items-center diamond-btn-sm'>
                              <span className='font-normal text-[18px] text-[rgba(223, 248, 255, 1)]'>Cancel</span>
                            </div>
                          </Button>
                        </KeyboardControl>
                      </ControlHighlight>
                      <ControlHighlight
                        active={keyboardControls.isEnabled && keyboardControls.isElementFocused(idButtonSave)}
                        highlightEffect={HighlightEffect.ButtonHexOutline}
                      >
                        <KeyboardControl>
                          <Button
                            id={idButtonSave}
                            data-testid={idButtonSave}
                            type='submit'
                            wrapperClass='w-full ml-4'
                            className='w-full diamond-btn-sm h-[32px] font-bold mt-[16px]'
                            bgColor='bg-dopage-darkGreen'
                          >
                            {isSubmitting
                              ? (
                                <div className='flex justify-center'>
                                  <ReactLoading type='bubbles' color='black' height={32} width={32} />
                                </div>
                                )
                              : (
                                <span className='font-normal text-[18px] text-neutral-900'>Save</span>
                                )}
                          </Button>
                        </KeyboardControl>
                      </ControlHighlight>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EditLine
